import { useNavigate } from "react-router-dom";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "@/design/components/Avatar";
import { useStore, useSelectableProfiles } from "@/domain/store";
import { useActiveProfile } from "@/domain/hooks";
import { useAuthStore } from "@/domain/authStore";
import { profileSection, sectionLabels, sectionOrder } from "@/domain/seed/profiles";
import type { Profile } from "@/domain/types";

export function ProfileSwitcher() {
  const active = useActiveProfile();
  const profiles = useSelectableProfiles();
  const setProfile = useStore((s) => s.setProfile);
  const devMode = useStore((s) => s.devMode);
  const toggleDevMode = useStore((s) => s.toggleDevMode);
  const resetWorkspaceData = useStore((s) => s.resetWorkspaceData);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  function choose(p: Profile) {
    // Re-selecting the already-active profile (the "Current profile" row,
    // always shown first) must be a no-op - it previously navigated to
    // p.landing unconditionally, so a mobile user tapping their own avatar
    // just to check who they were signed in as got yanked away from
    // whatever page/in-progress form they were on to their landing page,
    // with no actual profile switch to justify it.
    if (p.id === active.id) return;
    setProfile(p.id);
    toast.success(`Switched to ${p.organisation} (${p.displayName})`);
    navigate(p.landing);
  }

  // Group the non-active profiles by display section (spec §1 user types:
  // prime and sub contractors get their own headers), in display order.
  const grouped = sectionOrder
    .map((g) => ({ group: g, items: profiles.filter((p) => profileSection(p) === g) }))
    .filter((s) => s.items.length > 0);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="flex items-center gap-2 rounded-md p-0.5 pr-1.5 transition-colors hover:bg-subtle" aria-label="Switch profile">
          <Avatar initials={active.avatarInitials} size={32} />
          <ChevronDown className="h-3 w-3 text-faint" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 max-h-[80vh] w-80 overflow-y-auto rounded-lg border border-line bg-card p-1 shadow-overlay animate-fade-in"
        >
          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-faint">
            Current profile
          </DropdownMenu.Label>
          <ProfileMenuItem profile={active} active onSelect={() => choose(active)} />

          {grouped.map((section) => {
            const items = section.items.filter((p) => p.id !== active.id);
            if (items.length === 0) return null;
            return (
              <div key={section.group}>
                <DropdownMenu.Separator className="my-1 h-px bg-hairline" />
                <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-faint">
                  {sectionLabels[section.group]}
                </DropdownMenu.Label>
                {items.map((p) => (
                  <ProfileMenuItem key={p.id} profile={p} onSelect={() => choose(p)} />
                ))}
              </div>
            );
          })}

          <DropdownMenu.Separator className="my-1 h-px bg-hairline" />
          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-faint">
            Demo controls
          </DropdownMenu.Label>
          <DropdownMenu.Item
            onSelect={(e) => {
              e.preventDefault();
              toggleDevMode();
              toast.message(devMode ? "Demo mode off" : "Demo mode on");
            }}
            className="flex cursor-pointer items-center justify-between rounded-md px-2 py-2 text-sm text-mute outline-none data-[highlighted]:bg-subtle"
          >
            <span>Demo mode</span>
            <span className="text-xs text-faint">{devMode ? "On" : "Off"}</span>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => {
              resetWorkspaceData();
              toast.success("Workspace data reset to seed");
            }}
            className="cursor-pointer rounded-md px-2 py-2 text-sm text-mute outline-none data-[highlighted]:bg-subtle"
          >
            Reset workspace data
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => { logout(); navigate("/login", { replace: true }); }}
            className="cursor-pointer rounded-md px-2 py-2 text-sm text-mute outline-none data-[highlighted]:bg-subtle"
          >
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function ProfileMenuItem({
  profile,
  active,
  onSelect,
}: {
  profile: Profile;
  active?: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 outline-none data-[highlighted]:bg-subtle"
    >
      <Avatar initials={profile.avatarInitials} size={32} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">{profile.displayName}</p>
        <p className="truncate text-xs text-faint">
          {profile.role} · {profile.organisation}
        </p>
      </div>
      {active && <Check className="h-4 w-4 shrink-0 text-brand" />}
    </DropdownMenu.Item>
  );
}
